import { Button, Flex, Form, Input, List, Typography } from 'antd';
import { useEffect, useState } from 'react';
import {
  useCreateDepartmentMutation,
  useDeleteDepartmentMutation,
  useGetAllDepartmentsQuery,
  useUpdateDepartmentMutation,
} from '../../../../../../app/features/employee/emp-dashboard/emp-dashboard.api';
import { XIcon } from '@/icons';
import CustomModal, {
  type CustomModalProps,
} from '../../../../../../components/CustomModal';
import useAuth from '../../../../../../hooks/useAuth';
import useError from '../../../../../../hooks/useError';
import { toast } from '../../../../../../lib/toast';

type ConfirmModalProps = {
  open: boolean;
} & CustomModalProps;

export default function ManageDepartment({
  open,
  handleClose,
  ...rest
}: ConfirmModalProps) {
  const [form] = Form.useForm();
  const { tenantId, organizationId } = useAuth();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<Record<string, string>>(
    {},
  );

  const {
    data,
    isLoading: listLoading,
    isFetching: listFetching,
    error: listError,
    refetch,
  } = useGetAllDepartmentsQuery(
    { params: { page: 1, limit: 100, status: 'active' } },
    { skip: !open },
  );

  const [createDepartment, { isLoading: creating, error: createError }] =
    useCreateDepartmentMutation();
  const [updateDepartment, { isLoading: updating, error: updateError }] =
    useUpdateDepartmentMutation();
  const [deleteDepartment, { isLoading: removing, error: deleteError }] =
    useDeleteDepartmentMutation();

  useError(listError, 'Failed to load departments');
  useError(createError, 'Failed to add department');
  useError(updateError, 'Failed to update department');
  useError(deleteError, 'Failed to delete department');

  // Initialize editing values when data loads
  useEffect(() => {
    if (data?.data?.data) {
      const values = data.data.data.reduce((acc, dept) => {
        acc[dept.id] = dept.name;
        return acc;
      }, {} as Record<string, string>);
      setEditingValues(values);
    }
  }, [data?.data]);

  const handleFinish = async (values: any) => {
    const orgId = organizationId || tenantId;
    if (!values?.name) return;
    try {
      await createDepartment({
        name: values.name,
        organization_id: orgId,
      }).unwrap();
      toast('success', 'Department added');
      form.resetFields();
      refetch();
    } catch (_err) {
      // error handled by useError hook
    }
  };

  const handleUpdate = async (id: string, value: string) => {
    const dept = data?.data?.data.find((d) => d.id === id);

    // Trim the value to handle whitespace
    const trimmedValue = value.trim();

    // Reset to original value if empty or unchanged
    if (
      !trimmedValue ||
      !dept?.id ||
      !dept?.name ||
      dept?.name === trimmedValue
    ) {
      if (dept?.name) {
        setEditingValues((prev) => ({ ...prev, [id]: dept.name }));
      }
      return;
    }

    setActiveId(id);
    try {
      await updateDepartment({
        id,
        data: { name: trimmedValue },
      }).unwrap();
      toast('success', 'Department updated');
      refetch();
    } catch (_err) {
      // error handled by useError hook
      // Reset to original value on error
      if (dept?.name) {
        setEditingValues((prev) => ({ ...prev, [id]: dept.name }));
      }
    } finally {
      setActiveId(null);
    }
  };

  const handleDelete = async (id: string) => {
    setActiveId(id);
    try {
      await deleteDepartment({ id }).unwrap();
      toast('success', 'Department removed');
      refetch();
    } catch (_err) {
      // error handled by useError hook
    } finally {
      setActiveId(null);
    }
  };

  return (
    <>
      <CustomModal
        width={450}
        open={open}
        handleClose={() => {
          form.resetFields();
          handleClose();
        }}
        title={'Edit Department'}
        {...rest}
      >
        <div className="px-4 pb-6 pt-4">
          <Form
            form={form}
            name="edit-dept"
            onFinish={handleFinish}
            layout="vertical"
          >
            <div className=" gap-3">
              <Form.Item
                name={['name']}
                label="Department Name"
                rules={[
                  { required: true, message: 'Please enter department name' },
                ]}
              >
                <Input
                  placeholder="Department Name"
                  className="mt-2 bg-transparent border border-gray-700 rounded-md h-10 text-sm text-gray-200 placeholder-gray-500"
                />
              </Form.Item>

              <Flex align="center" justify="end" gap={16} className="mt-6">
                <Button
                  type="default"
                  htmlType="submit"
                  loading={creating}
                  className="!bg-transparent !text-teal-500 !border !border-teal-500"
                >
                  Add
                </Button>
              </Flex>
            </div>
          </Form>
          <div className="mt-4">
            <Typography.Text className="text-white text-lg font-medium">
              Departments ({data?.data?.data?.length || 0})
            </Typography.Text>
          </div>
          <List
            loading={listLoading || listFetching}
            dataSource={data?.data?.data}
            renderItem={(item, index) => (
              <List.Item
                key={item?.id ?? index}
                className="py-3 !bg-transparent !border-b border-gray-800"
                style={{ background: '#0b1317' }}
                actions={[
                  <Button
                    key="delete"
                    size="small"
                    color="default"
                    htmlType="button"
                    variant="text"
                    loading={removing && activeId === item.id}
                    className="rounded shrink-0"
                    icon={<XIcon size={20} />}
                    classNames={{ icon: 'flex items-center justify-center' }}
                    onClick={() => handleDelete(item.id)}
                  />,
                ]}
              >
                <Input
                  variant="borderless"
                  value={editingValues[item.id] ?? item.name}
                  onChange={(e) =>
                    setEditingValues((prev) => ({
                      ...prev,
                      [item.id]: e.target.value,
                    }))
                  }
                  disabled={updating && activeId === item.id}
                  onBlur={(e) => handleUpdate(item.id, e.target.value)}
                  className="text-white text-base font-normal h-8"
                />
              </List.Item>
            )}
          />
        </div>
      </CustomModal>
    </>
  );
}
